import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const MaintenanceModal = ({ isOpen, onClose, truck, onSuccess, language = 'pt-BR' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'preventiva',
    descricao: '',
    data_inicio: '',
    data_fim_prevista: '',
    responsavel: '',
    custo: '',
    observacoes: ''
  });

  const handleSubmit = async () => {
    if (!formData.tipo || !formData.descricao || !formData.data_inicio || !formData.responsavel) {
      toast.error(language === 'pt-BR' ? 'Preencha todos os campos obrigatórios' : 'Fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.post(
        `${API_URL}/api/trucks/${truck.id}/maintenance`,
        {
          tipo: formData.tipo,
          descricao: formData.descricao,
          data_inicio: formData.data_inicio,
          data_fim_prevista: formData.data_fim_prevista || null,
          responsavel: formData.responsavel,
          custo: parseFloat(formData.custo) || 0,
          observacoes: formData.observacoes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(language === 'pt-BR' ? '✅ Manutenção registrada!' : '✅ Maintenance registered!');
      
      // Reset form
      setFormData({
        tipo: 'preventiva',
        descricao: '',
        data_inicio: '',
        data_fim_prevista: '',
        responsavel: '',
        custo: '',
        observacoes: ''
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao registrar manutenção' : 'Error registering maintenance');
    } finally {
      setLoading(false);
    }
  };

  if (!truck) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🔧 {language === 'pt-BR' ? 'Registrar Manutenção' : 'Register Maintenance'} - {truck.modelo} ({truck.placa})</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>{language === 'pt-BR' ? 'Tipo de Manutenção' : 'Maintenance Type'} *</Label>
            <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="preventiva">🔧 {language === 'pt-BR' ? 'Preventiva' : 'Preventive'}</SelectItem>
                <SelectItem value="corretiva">⚙️ {language === 'pt-BR' ? 'Corretiva' : 'Corrective'}</SelectItem>
                <SelectItem value="emergencial">🚨 {language === 'pt-BR' ? 'Emergencial' : 'Emergency'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Descrição' : 'Description'} *</Label>
            <Textarea
              value={formData.descricao}
              onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Descreva o que será feito...' : 'Describe what will be done...'}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Data Início' : 'Start Date'} *</Label>
              <Input
                type="date"
                value={formData.data_inicio}
                onChange={(e) => setFormData({...formData, data_inicio: e.target.value})}
              />
            </div>
            <div>
              <Label>{language === 'pt-BR' ? 'Previsão Término' : 'Expected End'}</Label>
              <Input
                type="date"
                value={formData.data_fim_prevista}
                onChange={(e) => setFormData({...formData, data_fim_prevista: e.target.value})}
              />
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Responsável (Oficina/Mecânico)' : 'Responsible (Workshop/Mechanic)'} *</Label>
            <Input
              value={formData.responsavel}
              onChange={(e) => setFormData({...formData, responsavel: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Ex: Oficina Centro Auto' : 'Ex: Central Auto Workshop'}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Custo Estimado' : 'Estimated Cost'}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.custo}
                onChange={(e) => setFormData({...formData, custo: e.target.value})}
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Observações' : 'Notes'}</Label>
            <Textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Observações adicionais...' : 'Additional notes...'}
              rows={2}
            />
          </div>

          <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
            <p className="text-sm text-yellow-800">
              ⚠️ {language === 'pt-BR' 
                ? 'O caminhão ficará indisponível até a conclusão da manutenção.'
                : 'The truck will be unavailable until maintenance is completed.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-orange-600 hover:bg-orange-700">
            {loading ? (language === 'pt-BR' ? 'Registrando...' : 'Registering...') : (language === 'pt-BR' ? '🔧 Registrar Manutenção' : '🔧 Register Maintenance')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceModal;